import { useParams } from 'react-router-dom';
import { useDealstackFeatureFlag } from '@dealstack/frontend/shared/hooks/useDealstackFeatureFlag/useDealstackFeatureFlag.hook';
import { FEATURE_FLAG } from '@dealstack/shared/lib/common/featureFlag';

interface Options {
	/** Wins over the route's `mipId`, for a surface that knows its programme explicitly. */
	mipId?: string;
	/** For the organisation-defaults tier, whose route carries no programme. */
	organizationId?: string;
}

/**
 * DC-825: whether KYC document certification is switched on, for the surfaces that have to be
 * hidden outright — the per-proof "require a certified copy" switches and the wording editor.
 *
 * Everything that merely *reads* certification needs nothing: the backend masks the three
 * certification flags out of the KYC-setting response while the flag is off, so the upload modal,
 * the "Not certified" badge and the manager's review panel all go dark on their own.
 *
 * Sends the same single attribute the backend does — `mipId`, or `organizationId` where there is
 * no programme — so a targeting rule can never open one side without the other.
 */
export const useKycCertificationEnabled = ({ mipId, organizationId }: Options = {}) => {
	const params = useParams();
	const programmeId = mipId ?? params.mipId;
	const attributes: Record<string, string> = {};
	if (programmeId) {
		attributes['mipId'] = programmeId;
	} else if (organizationId) {
		attributes['organizationId'] = organizationId;
	}

	const { value, loading } = useDealstackFeatureFlag(
		FEATURE_FLAG.KYC_DOCUMENT_CERTIFICATION,
		attributes
	);

	return { certificationEnabled: !!value, loading };
};
